// Product analytics plumbing (Posthog).
//
// Symmetric to `telemetry.ts` (Sentry, ADR 0006):
//
//   VITE_POSTHOG_KEY         — your Posthog project API key (already in env.ts)
//   VITE_ENABLE_TELEMETRY    — shared master kill switch for Sentry + Posthog
//
// On localhost with no `.env.local` both are unset → `initAnalytics()`
// returns early → zero network traffic → Posthog's pageview / autocapture
// hooks never install.
//
// In staging / production, set both in your deploy platform's secret
// manager. Same `VITE_ENABLE_TELEMETRY` kill-switch as Sentry means
// turning off all external reporting is a one-line env change.
//
// ADR: docs/adr/0014-posthog-analytics.md
// Related: prof-backlog #7, kondo-parity.md §5.5.4.

import posthog from 'posthog-js'
import { env } from './env'

// ─── State ───────────────────────────────────────────────────────────────────

let _initialized = false

// ─── Gate ────────────────────────────────────────────────────────────────────

/**
 * True when both `VITE_POSTHOG_KEY` and `VITE_ENABLE_TELEMETRY` are set.
 * Mirrors `isTelemetryEnabled` in `telemetry.ts` — callers can decide
 * whether to shape a props object before spending the work.
 */
export function isAnalyticsEnabled(): boolean {
  if (!env.posthogKey) return false
  const flag = (import.meta.env as Record<string, string | undefined>).VITE_ENABLE_TELEMETRY
  return flag === 'on' || flag === 'true' || flag === '1'
}

// ─── Init ────────────────────────────────────────────────────────────────────

/**
 * Initialise Posthog if both gate env vars are set. Idempotent — safe to
 * call multiple times.
 *
 * Config notes:
 *   - `capture_pageview: false` — we'll call `track('page_loaded', ...)`
 *     explicitly when we want to, not on every route flip. Auto-capture
 *     pageview would double-count with any client-side route instrumentation.
 *   - `autocapture: false` — we rely on explicit `track()` calls so there's
 *     one place that controls what reaches Posthog. Reduces surprise data
 *     volume and keeps the dashboard interpretable.
 *   - `persistence: 'localStorage+cookie'` — Posthog default; the identifier
 *     survives across sessions so returning users don't spawn new IDs.
 *   - `disable_session_recording: true` — recording is expensive + privacy-
 *     sensitive. Flip on per-page later if we want it somewhere specific.
 */
export function initAnalytics(): void {
  if (_initialized) return
  if (!isAnalyticsEnabled()) return

  posthog.init(env.posthogKey!, {
    api_host: 'https://us.i.posthog.com', // default; override via env when we self-host / reverse-proxy
    capture_pageview: false,
    autocapture: false,
    disable_session_recording: true,
    // Environment tag on every event so dev / staging / prod don't commingle
    // in the dashboard. `env.appEnv` comes from VITE_APP_ENV + ADR 0012.
    loaded: (p) => p.register({ env: env.appEnv }),
  })

  _initialized = true
}

/**
 * Tear down for tests + hot-reload. Not called in production flows.
 */
export function _resetForTests(): void {
  _initialized = false
}

// ─── User identity ───────────────────────────────────────────────────────────

/**
 * Tag subsequent events with the signed-in user's id. Call from
 * `AuthProvider` when a session appears. Pass `null` on sign-out.
 *
 * Same restraint as `setTelemetryUser` (ADR 0006) — no email / display_name
 * / avatar are sent. The id is enough to correlate events to a user
 * without exposing PII in the Posthog UI.
 */
export function setAnalyticsUser(user: { id: string } | null): void {
  if (!_initialized) return
  if (user == null) {
    posthog.reset()
    return
  }
  posthog.identify(user.id)
}

// ─── Tracking ────────────────────────────────────────────────────────────────

/**
 * Core event vocabulary. Keep this typed — adding events should be a
 * deliberate decision, not a sprinkled `track('some-string')` at a call
 * site. The event name is used verbatim in Posthog dashboards; renaming
 * later means historical data is split across names.
 *
 * Naming convention: `object_verb` in snake_case. Past tense for
 * completed actions (`template_sent`), present for state changes
 * (`filter_changed`).
 */
export type AnalyticsEvent =
  | 'app_signed_in'
  | 'app_signed_out'
  | 'inbox_loaded'
  | 'conversation_opened'
  | 'message_sent'
  | 'search_performed'
  | 'reminder_set'
  | 'template_sent'
  | 'crm_action_taken'
  | 'filter_changed'
  | 'tag_added'
  | 'tag_removed'

/**
 * Fire an analytics event. No-op when analytics isn't initialised —
 * fire-and-forget semantic at every call site. Props are optional but
 * should be kept small (single-level, no objects).
 *
 * For ad-hoc instrumentation during a feature's dev cycle, use
 * `reportMessage` in telemetry.ts instead — that lives in Sentry which
 * has a retention horizon, rather than accumulating forever in Posthog.
 */
export function track(
  event: AnalyticsEvent,
  props?: Record<string, string | number | boolean | null | undefined>,
): void {
  if (!_initialized) return
  posthog.capture(event, props)
}
